import { useQuery, useQueryClient } from '@tanstack/react-query'
import { compact, sortBy, uniqBy } from 'lodash'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { usePageTitle } from '../../hooks/usePageTitle'
import { Booking, MAX_HOURLY_DURATION } from '../../models/Booking'
import { Package } from '../../models/Package'
import { RecordFactory } from '../../models/RecordFactory'
import { Source } from '../../models/Source'
import { User } from '../../models/User'
import { RecordNotFoundError } from '../../models/errors/RecordNotFoundError'
import { useHistoryTrackerContext } from '../../providers/HistoryTrackerProvider'
import { PERM_ROLE_EMPLOYEE } from '../../utils'
import { isPast } from '../../utils/dates'
import { bookingLateInfo, cancellationFeeString } from '../../utils/records'
import { ComponentData } from '../../utils/typing'
import { PocketComponent, PocketComponentProps } from '../PocketComponent'
import { usePSComponent } from '../PocketComponent'
import { PSApplication } from '../app/PSApplication'
import { CancellationModal } from '../common/CancellationModal'
import { ImageView } from '../common/ImageView'
import { SelectModal } from '../common/SelectModal'
import { ConfirmationModal } from '../common/modals/ConfirmationModal'
import { BaseRecordView, HeaderButton } from '../common/sections'
import { SourceModalWorkflow } from '../contacts/SourceModalWorkflow'
import { BookingView } from './BookingView'

const hasBookingPermission = (
    booking: Booking,
    level:
        | typeof User.PERM_LEVEL_VIEW
        | typeof User.PERM_LEVEL_EDIT
        | typeof User.PERM_LEVEL_DELETE
        | typeof User.PERM_LEVEL_OWNER
) => {
    const loggedInUser = PSApplication.me()
    if (!booking || !loggedInUser) return false

    const perm = loggedInUser.perm
    const owner: User | undefined = 'owner' in booking ? (booking.owner as User) : undefined
    const client: User | undefined = 'client' in booking ? (booking.client as User) : undefined
    const person: User | undefined = 'person' in booking ? (booking.person as User) : undefined
    const employee: User | undefined =
        'employee' in booking ? (booking.employee as User) : undefined

    if (owner?.id === loggedInUser.id) {
        return true
    }
    else if (level === User.PERM_LEVEL_OWNER) {
        return false
    }
    if (
        !!owner &&
        perm === User.PERM_ROLE_ADMIN &&
        (loggedInUser.employer?.id ?? loggedInUser.id) === owner.id
    ) {
        return true
    }
    if (perm === User.PERM_ROLE_EMPLOYEE && !!employee && employee?.id === loggedInUser.id) {
        return level < User.PERM_LEVEL_DELETE
    }

    return (
        ((!!person && person?.id === loggedInUser.id) ||
            (!!client && client?.id === loggedInUser.id)) &&
        level <= User.PERM_LEVEL_VIEW
    )
}

const viewWillAppear = async ({ urlParams, owner }: PocketComponent) => {
    if (!urlParams.id) {
        throw new RecordNotFoundError('Booking not found')
    }

    // TODO: consider fetchAndLoad helper

    let booking = await RecordFactory.getInstance().loadBooking(urlParams.id)

    if (!booking) {
        throw new RecordNotFoundError('Booking not found')
    }

    const forVendor = owner?.id === booking?.owner?.id

    if ((await RecordFactory.getInstance().fetchBooking('view', booking, forVendor)).ok) {
        booking = await RecordFactory.getInstance().loadBooking(urlParams.id)
    }

    if (!booking) {
        throw new RecordNotFoundError('Booking not found')
    }

    const client = booking.client
    if (!client) {
        throw new RecordNotFoundError('Booking not found')
    }

    const customFields = booking.vendor?.id
        ? await RecordFactory.getInstance().loadBookingFields(booking.vendor.id, false, false)
        : []

    // Need to load items to re-populate the items cache after reload for line items
    if (booking.owner?.id) await RecordFactory.getInstance().loadItems(booking.owner.id)

    return { booking, client, forVendor, customFields }
}

const render = ({
    data,
    base: { me, owner, logAndDisplayError, reload },
}: PocketComponentProps<ComponentData<typeof viewWillAppear>>) => {
    if (data instanceof RecordNotFoundError) {
        throw data
    }

    const { client, forVendor, customFields } = data

    const [booking, setBooking] = useState(data.booking)
    const [isPackagesModalOpen, setIsPackagesModalOpen] = useState(false)

    const navigate = useNavigate()

    usePageTitle('Booking')

    const { findLastHistoryEntry } = useHistoryTrackerContext()

    const [confirmationModalData, setConfirmationModalData] = useState<{
        title: string
        confirmationButtonText: string
        body: string
        onConfirm: () => void
        secondaryButton?: {
            title: string
            onClick: () => void
        }
        isDestructive?: boolean
        size?: 'medium' | 'large'
    } | null>(null)

    const [isSourcesModalOpen, setIsSourcesModalOpen] = useState(false)

    const queryClient = useQueryClient()
    const { data: clientSources } = useQuery({
        queryKey: ['sources', client.id],
        queryFn: () => Source.validClientSources(client.id ?? ''),
    })

    const { data: applicablePackages } = useQuery({
        queryKey: ['packages', booking.client, booking.item, booking.package],
        queryFn: async () => {
            if (booking.client === undefined) {
                return []
            }

            const result = await RecordFactory.getInstance().fetchPaidPackages(
                booking.client,
                booking.item
            )
            if (!result.ok) {
                throw result.error
            }
            return result.value
        },
    })
    const { data: packageItems } = useQuery({
        queryKey: ['packagesWithMetadata', booking.package, applicablePackages],
        queryFn: async () => {
            if (applicablePackages === undefined) {
                return []
            }

            const packages = uniqBy(
                compact([booking.package, ...applicablePackages]),
                (packageObj) => packageObj.id
            )

            return sortBy(
                (
                    await Promise.all(
                        packages.map(async (packageObj) => ({
                            packageObj,
                            available: (await packageObj.available()) ?? 0,
                        }))
                    )
                ).map(({ packageObj, available }) => {
                    const { capacityType, capacity, name } = packageObj

                    const remainingDisplayString = Package.capacityDisplayString(
                        capacityType ?? 'session',
                        available ?? capacity ?? 0
                    )

                    return {
                        id: packageObj.id ?? '',
                        title: packageObj.name ?? '',
                        subtitle: `${name} · ${remainingDisplayString} remaining`,
                        tertiaryView: (
                            <ImageView
                                image={packageObj.item?.image}
                                fallbackIcon={{
                                    icon:
                                        packageObj.item?.iconNameAndCategoryString ??
                                        'package.small',
                                    background: 'round',
                                    className: 'size-12',
                                }}
                                className="size-12 rounded-full"
                            />
                        ),
                        packageObj,
                    }
                }),
                (packageItem) => packageItem.title
            )
        },
    })
    const selectedPackageItems = (packageItems ?? []).filter(
        (packageItem) => packageItem.id === booking.package?.id
    )

    const forReserve =
        booking.type === 'reservation' ||
        booking.item?.type === 'reservation' ||
        (booking.duration ?? 0) > MAX_HOURLY_DURATION

    const handleSelectPackageItems = async (items: NonNullable<typeof packageItems>) => {
        const newPackageObj = items[0]?.packageObj
        if (newPackageObj === undefined) {
            return
        }

        const result = await RecordFactory.getInstance().applyPackage(booking, newPackageObj)
        if (!result.ok) {
            logAndDisplayError(result.error)
            return
        }
        setBooking(result.value)
    }

    const handleConfirm = () => {
        setConfirmationModalData({
            title: `Confirm ${booking.schedule ? 'appointments' : 'appointment'}`,
            body: `Would you like to confirm ${booking.schedule ? 'these appointments' : 'this appointment'}?`,
            confirmationButtonText: 'Confirm',
            onConfirm: async () => {
                setConfirmationModalData(null)
                const result = await RecordFactory.getInstance().confirmBooking(
                    booking,
                    false /* todo confirm all */,
                    false,
                    undefined
                )
                if (!result.ok) {
                    logAndDisplayError(result.error)
                    return
                }
                reload()
            },
        })
    }

    const handleResend = async () => {
        setConfirmationModalData({
            title: 'Resend appointment',
            body: 'Would you like to resend this appointment to this client?',
            confirmationButtonText: 'Resend',
            onConfirm: async () => {
                setConfirmationModalData(null)
                const result = await RecordFactory.getInstance().sendBooking(booking, true)
                if (!result.ok) {
                    logAndDisplayError(result.error)
                    return
                }
            },
        })
    }

    const handleCharge = () => navigate(`/charge/booking/${booking.id}/complete`)

    const [isCancellationModalOpen, setIsCancellationModalOpen] = useState(false)

    const handleDeleteSource = async (source: Source) => {
        if (source.id === undefined) {
            return
        }
        await RecordFactory.getInstance().deleteSource(source.id)
        await queryClient.invalidateQueries({ queryKey: ['sources', client.id] })
    }

    const handleSelectSource = async (source: Source) => {
        setIsSourcesModalOpen(false)
        const result = await RecordFactory.getInstance().changeBookingSource(booking, source)
        if (!result.ok) {
            logAndDisplayError(result.error)
            return
        }
        setBooking(result.value)
    }

    const handleCancel = async () => {
        if (!me || !booking.vendor) return

        const { isCancellableClass, isCancellableSeries, forClass, isLate } = bookingLateInfo(
            booking,
            booking.item,
            me,
            booking.vendor
        )

        if (forVendor && booking.confirmedDate) {
            setIsCancellationModalOpen(true)
            return
        }

        const isRateSet = !!Number(booking.vendor.cancellationRate)
        const offerCancelAll = isCancellableClass || isCancellableSeries
        const title =
            !forVendor && isLate && booking.source && booking.confirmedDate && isRateSet
                ? `Cancel and pay fee ${cancellationFeeString(booking.vendor)}`
                : isCancellableSeries || forClass
                  ? 'Cancel this appointment'
                  : 'Cancel appointment'

        let confirmationButtonText = offerCancelAll ? 'Cancel this and future dates' : title
        let secondaryButtonTitle = offerCancelAll ? title : undefined

        let body = offerCancelAll
            ? `If you would like to cancel only this appointment choose "${secondaryButtonTitle}". To cancel this and future appointments choose "${confirmationButtonText}".`
            : `If you would like to cancel this appointment choose "${confirmationButtonText}"`

        if (forVendor && !!booking.channel && !isCancellableClass && !isCancellableSeries) {
            secondaryButtonTitle = title
            confirmationButtonText = 'Cancel & notify'
            body = `If you would like to cancel this appointment and send a notification to the client select "Cancel & notify". To cancel the appointment without notifying the client select "${secondaryButtonTitle}".`
        }

        setConfirmationModalData({
            title: 'Cancel Appointment',
            body,
            confirmationButtonText,
            onConfirm: async () => cancelbooking(!booking.channel, offerCancelAll),
            secondaryButton: !!secondaryButtonTitle
                ? {
                      title: secondaryButtonTitle,
                      onClick: async () => cancelbooking(true, false),
                  }
                : undefined,
            isDestructive: true,
            size: 'medium',
        })
    }

    const cancelbooking = async (dontNotify: boolean, cancelAll: boolean) => {
        setConfirmationModalData(null)
        const result = await RecordFactory.getInstance().cancelBooking(
            booking,
            dontNotify,
            cancelAll
        )
        if (!result.ok) {
            logAndDisplayError(result.error)
            return
        }
        reload()
    }

    const handleRebook = () => {
        if (!booking.id) return
        navigate(`/bookings/${booking.id}/rebook`)
    }

    const handleUndoCheckin = async () => {
        const result = await RecordFactory.getInstance().undoCheckinBooking(booking)
        if (!result.ok) {
            logAndDisplayError(result.error)
            return
        }
        reload()
    }

    const handleUndoCheckout = async () => {
        const result = await RecordFactory.getInstance().undoCheckoutBooking(booking)
        if (!result.ok) {
            logAndDisplayError(result.error)
            return
        }
        reload()
    }

    const handleSendReceipt = () => {
        setConfirmationModalData({
            title: 'Send receipt',
            body: 'Would you like to send a receipt to this client?',
            confirmationButtonText: 'Send receipt',
            onConfirm: async () => {
                setConfirmationModalData(null)
                const result = await RecordFactory.getInstance().sendReceipt(booking)
                if (!result.ok) {
                    logAndDisplayError(result.error)
                    return
                }
                reload()
            },
        })
    }

    const handleInvoice = () => {
        const forVendor = owner?.id === booking?.owner?.id
        const defaultRoute = `/invoices/new?bookingId=${encodeURIComponent(booking.id ?? '')}`

        if (forVendor && booking.schedule?.createdDate && !booking.schedule?.interval) {
            const confirmationButtonText = 'Invoice entire series'

            setConfirmationModalData({
                title: 'Send Invoice',
                body: `If you would like to invoice the entire series choose "${confirmationButtonText}".`,
                confirmationButtonText,
                onConfirm: () => navigate(`${defaultRoute}&shouldIncludeSchedule=true`),
                secondaryButton: {
                    title: 'Invoice this appointment',
                    onClick: () => navigate(`${defaultRoute}&shouldIncludeSchedule=false`),
                },
            })
        }
        else {
            navigate(defaultRoute)
        }
    }

    const handleUndoMarkPaid = () => {
        setConfirmationModalData({
            title: 'Undo mark paid',
            body: 'Would you like to undo this payment?',
            confirmationButtonText: 'Undo mark paid',
            onConfirm: async () => {
                setConfirmationModalData(null)
                const result = await RecordFactory.getInstance().rollbackTransaction(booking)
                if (!result.ok) {
                    logAndDisplayError(result.error)
                    return
                }
                reload()
            },
            isDestructive: true,
        })
    }

    const handleUndoCancel = () => {
        setConfirmationModalData({
            title: 'Undo cancellation',
            body: 'Would you like to undo the appointment cancellation?',
            confirmationButtonText: 'Undo cancellation',
            onConfirm: async () => {
                setConfirmationModalData(null)
                const result = await RecordFactory.getInstance().rollbackTransaction(booking)
                if (!result.ok) {
                    logAndDisplayError(result.error)
                    return
                }
                reload()
            },
            isDestructive: true,
        })
    }

    const headerButtons = (() => {
        const buttons: HeaderButton[] = []

        if (!me || !owner) return buttons

        const canEdit = hasBookingPermission(booking, User.PERM_LEVEL_EDIT)

        if (
            forVendor &&
            !booking.cancelledDate &&
            !booking.completedDate &&
            me.perm >= PERM_ROLE_EMPLOYEE &&
            canEdit
        ) {
            buttons.push({ title: 'Edit', onClick: () => navigate(`/bookings/${booking.id}/edit`) })
        }

        if (forVendor && canEdit && booking.status === 'sentRequest') {
            buttons.push({ title: 'Confirm', intent: 'primary', onClick: handleConfirm })
        }
        else if (
            (!forVendor || canEdit) &&
            !booking.cancelledDate &&
            booking.status === 'sentQuote' &&
            (booking.deposit ?? 0) > 0 &&
            !booking.paidDepositDate
        ) {
            buttons.push({
                title: 'Confirm',
                intent: 'primary',
                onClick: () => navigate(`/charge/booking/${booking.id}/confirm`),
            })

            if (forVendor && !booking.paidDate && !booking.source && !!booking.channel) {
                buttons.push({ title: 'Resend', onClick: handleResend })
            }
        }
        else if (
            forVendor &&
            canEdit &&
            booking.confirmedDate &&
            !booking.cancelledDate &&
            isPast(booking.date) &&
            !booking.invoicedDate &&
            !booking.paidDate &&
            !booking.package &&
            (booking.rate === undefined || booking.chargeAmount > 0)
        ) {
            buttons.push({ title: 'Charge', intent: 'primary', onClick: handleCharge })
        }
        else if (
            forVendor &&
            canEdit &&
            booking.confirmedDate &&
            !booking.cancelledDate &&
            isPast(booking.date) &&
            !booking.invoicedDate &&
            !booking.paidDate &&
            (!!booking.package || !booking.rate || booking.chargeAmount <= 0)
        ) {
            buttons.push({ title: 'Mark paid', onClick: handleCharge })
        }
        else if (forVendor && canEdit && !booking.cancelledDate && !booking.completedDate) {
            if (!booking.confirmedDate) {
                buttons.push({ title: 'Confirm', intent: 'primary', onClick: handleConfirm })
            }

            if (!booking.paidDate && !booking.source && !!booking.channel) {
                buttons.push({ title: 'Resend', onClick: handleResend })
            }

            buttons.push({ title: 'Cancel', onClick: handleCancel })
        }
        else if (forVendor && canEdit && !booking.cancelledDate && booking.paidDate) {
            if (booking.type !== 'class') {
                buttons.push({
                    title: 'Rebook',
                    longTitle: 'Rebook appointment',
                    onClick: handleRebook,
                })
            }

            buttons.push({
                title: 'Receipt',
                longTitle: 'Send receipt',
                onClick: handleSendReceipt,
            })
        }
        else if ((!forVendor || canEdit) && booking.status === 'sentQuote') {
            buttons.push({ title: 'Confirm', intent: 'primary', onClick: handleConfirm })
        }

        if (
            forVendor &&
            booking.id &&
            booking.confirmedDate &&
            !booking.paidDate &&
            !booking.invoicedDate &&
            !booking.package
        ) {
            buttons.push({ title: 'Invoice', onClick: handleInvoice })
        }

        if (forVendor && booking.completedDate) {
            if (booking.markPaidDate) {
                buttons.push({ title: 'Undo', longTitle: 'Undo paid', onClick: handleUndoMarkPaid })
            }

            if (booking.cancelledDate) {
                buttons.push({
                    title: 'Undo',
                    longTitle: 'Undo cancel',
                    onClick: handleUndoCancel,
                })
            }
        }
        else if (booking.status === 'sentRequest') {
            if (
                forVendor &&
                owner.allowPackage &&
                me.perm > User.PERM_ROLE_CONTRACTOR &&
                (packageItems?.length ?? 0) > 0
            ) {
                buttons.push({
                    title: 'Package',
                    longTitle: 'Apply package',
                    onClick: () => setIsPackagesModalOpen(true),
                })
            }
        }
        else if (forVendor && booking.cancelledDate) {
            buttons.push({ title: 'Undo', longTitle: 'Undo cancel', onClick: handleUndoCancel })
        }
        else if (booking.status !== 'waitListed') {
            if (
                forVendor &&
                owner.allowPackage &&
                me.perm > User.PERM_ROLE_CONTRACTOR &&
                !booking.paidDepositDate &&
                (packageItems?.length ?? 0) > 0
            )
                buttons.push({
                    title: 'Package',
                    longTitle: 'Apply package',
                    onClick: () => setIsPackagesModalOpen(true),
                })
        }

        if (
            (!forVendor || canEdit) &&
            booking.id &&
            !booking.cancelledDate &&
            !booking.completedDate
        ) {
            buttons.push({
                title: 'Cancel',
                longTitle: 'Cancel appointment',
                onClick: handleCancel,
            })
        }

        if (
            (!forVendor || canEdit) &&
            booking.id &&
            booking.confirmedDate &&
            booking.type !== 'class'
        ) {
            buttons.push({
                title: 'Rebook',
                longTitle: 'Rebook appointment',
                onClick: handleRebook,
            })
        }

        if (
            booking.id &&
            !booking.completedDate &&
            !booking.capturedDate &&
            !booking.cancelledDate &&
            booking.confirmedDate
        )
            buttons.push({
                title: 'Card',
                longTitle: 'Change card',
                onClick: () => setIsSourcesModalOpen(true),
            })

        if (booking.allowUndoCheckin) {
            buttons.push({
                title: 'Undo check-in',
                longTitle: 'Undo check-in',
                onClick: handleUndoCheckin,
            })
        }

        if (booking.allowUndoCheckout) {
            buttons.push({
                title: 'Undo check-out',
                longTitle: 'Undo check-out',
                onClick: handleUndoCheckout,
            })
        }
        return buttons
    })()

    const backHistoryEntry = findLastHistoryEntry({
        excluding: /^(\/bookings\/.*)|(\/charge.*)|(\/invoices\/.*)|(.*\/booking\/.*)/,
    })

    return (
        <BaseRecordView
            title="Appointment"
            backButton={
                backHistoryEntry !== undefined
                    ? {
                          title: backHistoryEntry.title ?? 'Back',
                          onClick: backHistoryEntry.goBackTo,
                      }
                    : forReserve
                      ? {
                            title: 'Reservations',
                            onClick: () => navigate('/dashboards/reservations'),
                        }
                      : {
                            title: 'Bookings',
                            onClick: () => navigate('/dashboards/bookings'),
                        }
            }
            headerButtons={headerButtons}
            tag={booking.statusTag ?? undefined}
        >
            <BookingView booking={booking} customFields={customFields} />

            {confirmationModalData !== null && (
                <ConfirmationModal
                    isOpen
                    onClose={() => setConfirmationModalData(null)}
                    onConfirm={() => confirmationModalData.onConfirm()}
                    title={confirmationModalData.title}
                    cancelButtonText={confirmationModalData.secondaryButton?.title}
                    confirmButtonText={confirmationModalData.confirmationButtonText}
                    isDestructive={confirmationModalData.isDestructive}
                    shouldHideCancelButton={!confirmationModalData.secondaryButton}
                    onCancel={confirmationModalData.secondaryButton?.onClick}
                    size={confirmationModalData.size ?? 'small'}
                >
                    {confirmationModalData.body}
                </ConfirmationModal>
            )}

            {isCancellationModalOpen !== null && booking.vendor && (
                <CancellationModal
                    isOpen={isCancellationModalOpen}
                    onClose={() => setIsCancellationModalOpen(false)}
                    target={{
                        type: 'booking',
                        booking,
                        doNotCancelAll: false,
                    }}
                    vendor={booking.vendor}
                    me={me}
                    onCancellationComplete={() => {
                        setIsCancellationModalOpen(false)
                        reload()
                    }}
                />
            )}

            <SourceModalWorkflow
                isOpen={isSourcesModalOpen}
                onClose={() => setIsSourcesModalOpen(false)}
                sources={clientSources ?? []}
                onCreateSource={{
                    action: 'create-source',
                    client,
                    callback: () =>
                        queryClient.invalidateQueries({ queryKey: ['sources', client.id] }),
                }}
                onDeleteSource={handleDeleteSource}
                onSelectSource={handleSelectSource}
                selectedSourceID={booking.source?.id ?? null}
            />

            <SelectModal
                isOpen={isPackagesModalOpen}
                onClose={() => setIsPackagesModalOpen(false)}
                title="Select package"
                selectionType="single"
                items={packageItems ?? []}
                selectedItems={selectedPackageItems}
                onSelectItems={handleSelectPackageItems}
            />
        </BaseRecordView>
    )
}

export const BookingPage = usePSComponent(render, viewWillAppear)
